import { Suspense, useCallback, useEffect, useMemo, useRef } from "react"
import { keepPreviousData, useInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowDownUp, ArrowRight, Inbox as InboxIcon } from "lucide-react"
import { z } from "zod"
import type { InboxView, WorkSort, WorkView } from "@vienna/contracts"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import { CommentThreadView } from "@/components/comments/comment-detail"
import { InboxToolbar, type Filters } from "@/components/inbox/inbox-toolbar"
import { Thread } from "@/components/inbox/thread"
import { WorkRow } from "@/components/inbox/work-row"
import { channelsQuery, inboxViewsQuery, savedRepliesQuery, teamQuery, workItemsQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

const Search_ = z.object({
  c: z.string().optional(),
  k: z.enum(["conversation", "comment"]).optional(),
  view: z.enum(["attention", "mine", "unassigned", "waiting", "done", "automation", "all"]).optional(),
  type: z.enum(["all", "messages", "comments"]).optional(),
  accounts: z.string().optional(),
  assignee: z.string().optional(),
  origin: z.enum(["organic", "ad"]).optional(),
  visibility: z.enum(["visible", "hidden", "deleted", "any"]).optional(),
  post: z.string().optional(),
  unread: z.enum(["true"]).optional(),
  category: z.enum(["spam", "scam", "targeted_hate", "harassment", "threat", "graphic_violence", "sexual", "profanity", "self_promotion", "off_topic", "complaint", "question", "purchase_intent", "praise"]).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  q: z.string().optional(),
  sort: z.enum(["oldest_unanswered", "newest_activity"]).optional(),
  saved: z.string().optional(),
  /** Older links used queue names; they map onto views. */
  queue: z.enum(["all", "mine", "unassigned", "automation", "handover", "resolved"]).optional(),
})
type Search = z.infer<typeof Search_>

const LEGACY_VIEWS: Record<NonNullable<Search["queue"]>, WorkView> = { all: "all", mine: "mine", unassigned: "unassigned", automation: "automation", handover: "attention", resolved: "done" }

export const Route = createFileRoute("/w/$workspace/inbox")({
  validateSearch: Search_,
  loader: ({ context, params }) => Promise.all([context.queryClient.ensureQueryData(teamQuery(params.workspace)), context.queryClient.ensureQueryData(savedRepliesQuery(params.workspace)), context.queryClient.ensureQueryData(channelsQuery(params.workspace)), context.queryClient.ensureQueryData(inboxViewsQuery(params.workspace))]),
  component: Inbox,
})

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) || target.closest("[role=dialog], [role=menu]") !== null
}

/** One inbox over private messages and public comments: views, filters and sort are server-backed, so counts never hide older work. */
function Inbox() {
  const { workspace } = useWorkspaceContext()
  const navigate = useNavigate()
  const search = Route.useSearch()
  const channels = useSuspenseQuery(channelsQuery(workspace.slug)).data
  const team = useSuspenseQuery(teamQuery(workspace.slug)).data
  const views = useSuspenseQuery(inboxViewsQuery(workspace.slug)).data
  const params = { workspace: workspace.slug }
  const searchRef = useRef<HTMLInputElement>(null)

  const filters: Filters = useMemo(
    () => ({ view: search.view ?? (search.queue ? LEGACY_VIEWS[search.queue] : "attention"), type: search.type ?? "all", accounts: search.accounts, assignee: search.assignee, origin: search.origin, visibility: search.visibility, post: search.post, unread: search.unread, category: search.category, from: search.from, to: search.to, q: search.q }),
    [search.view, search.queue, search.type, search.accounts, search.assignee, search.origin, search.visibility, search.post, search.unread, search.category, search.from, search.to, search.q]
  )
  const activeView = views.find((v) => v.id === search.saved)
  const apiFilters = useMemo(() => ({ ...filters, sort: search.sort, limit: 50 }), [filters, search.sort])
  const page = useInfiniteQuery({
    ...workItemsQuery(workspace.slug, apiFilters),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
  })
  const head = page.data?.pages[0]
  const items = useMemo(() => page.data?.pages.flatMap((p) => p.items) ?? [], [page.data])
  const sort = search.sort ?? head?.sort ?? "newest_activity"
  const selectedId = search.c ?? null
  const selectedKind = search.k ?? "conversation"
  const noChannels = channels.connections.filter((c) => c.status !== "disconnected").length === 0
  const filtered = Boolean(filters.accounts || filters.assignee || filters.origin || filters.visibility || filters.post || filters.unread || filters.category || filters.from || filters.to || filters.q || filters.type !== "all")
  const welcome = page.isSuccess && head?.counts.all === 0 && !filtered && noChannels
  const outsideView = selectedId !== null && page.isSuccess && !page.hasNextPage && !items.some((i) => i.id === selectedId)

  const setSearch = useCallback((patch: Partial<Search>) => void navigate({ to: "/w/$workspace/inbox", params: { workspace: workspace.slug }, search: (prev) => ({ ...prev, ...patch }), replace: true }), [navigate, workspace.slug])
  const changeFilters = useCallback((patch: Partial<Filters> & { sort?: WorkSort | undefined }) => setSearch({ ...patch, queue: undefined, saved: undefined }), [setSearch])
  const reset = useCallback(() => setSearch({ view: undefined, type: undefined, accounts: undefined, assignee: undefined, origin: undefined, visibility: undefined, post: undefined, unread: undefined, category: undefined, from: undefined, to: undefined, q: undefined, sort: undefined, saved: undefined, queue: undefined }), [setSearch])
  const applyView = useCallback((view: InboxView) => setSearch({ ...view.filters, view: view.filters.view ?? "attention", type: view.filters.type ?? "all", sort: view.sort ?? undefined, saved: view.id, queue: undefined }), [setSearch])
  const select = useCallback((id: string, kind: "conversation" | "comment") => setSearch({ c: id, k: kind }), [setSearch])
  const back = useCallback(() => setSearch({ c: undefined, k: undefined }), [setSearch])

  // Keyboard: next and previous item, focus search. Never inside inputs, menus or dialogs.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return
      if (e.key === "/") {
        e.preventDefault()
        searchRef.current?.focus()
        return
      }
      const next = e.key === "j" || e.key === "ArrowDown" ? 1 : e.key === "k" || e.key === "ArrowUp" ? -1 : 0
      if (!next || !items.length) return
      e.preventDefault()
      const index = items.findIndex((i) => i.id === selectedId)
      const target = items[Math.min(items.length - 1, Math.max(0, (index === -1 ? (next === 1 ? -1 : items.length) : index) + next))]
      if (target) {
        select(target.id, target.kind)
        document.querySelector<HTMLElement>(`[data-item-id="${target.id}"]`)?.scrollIntoView({ block: "nearest" })
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [items, selectedId, select])

  return (
    <div className={`inbox-shell ${selectedId ? "has-selection" : ""}`}>
      <InboxToolbar filters={filters} sort={sort} counts={head?.counts} channels={channels.connections} members={team.members} views={views} activeView={activeView} searchRef={searchRef} onChange={changeFilters} onReset={reset} onApplyView={applyView} />
      <div className="inbox-columns">
        <aside className="conversation-list">
          <div className="list-tabs" role="tablist" aria-label="Quick filters">
            <button type="button" role="tab" aria-selected={!filters.unread && filters.type !== "comments" && filters.view !== "mine"} onClick={() => changeFilters({ unread: undefined, type: "all", ...(filters.view === "mine" ? { view: "attention" } : {}) })}>
              All <span>{head?.counts[filters.view === "mine" ? "attention" : filters.view] ?? ""}</span>
            </button>
            <button type="button" role="tab" aria-selected={Boolean(filters.unread)} onClick={() => changeFilters({ unread: "true", type: "all" })}>
              Unread <span>{head?.unread ?? ""}</span>
            </button>
            <button type="button" role="tab" aria-selected={filters.type === "comments" && !filters.unread} onClick={() => changeFilters({ type: "comments", unread: undefined })}>
              Comments <span>{head?.breakdown.comments ?? ""}</span>
            </button>
            <button type="button" role="tab" aria-selected={filters.view === "mine" && !filters.unread && filters.type !== "comments"} onClick={() => changeFilters({ view: "mine", unread: undefined, type: "all" })}>
              Assigned to me <span>{head?.counts.mine ?? ""}</span>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="list-sort" aria-label={`Sort: ${sort === "newest_activity" ? "newest first" : "oldest waiting first"}`}>
                  <ArrowDownUp size={13} />
                  {sort === "newest_activity" ? "Newest" : "Oldest waiting"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuRadioGroup value={sort} onValueChange={(v) => changeFilters({ sort: v as WorkSort })}>
                  <DropdownMenuRadioItem value="newest_activity">Newest activity first</DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="oldest_unanswered">Oldest waiting first</DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="conversation-rows" role="list" aria-label="Work items">
            {page.isPending && [1, 2, 3, 4].map((i) => <Skeleton key={i} className="mx-1 my-2 h-24" />)}
            {items.map((item) => (
              <WorkRow key={`${item.kind}:${item.id}`} item={item} selected={item.id === selectedId} onSelect={() => select(item.id, item.kind)} />
            ))}
            {page.isSuccess && items.length === 0 && !welcome && (
              <div className="list-empty">
                <InboxIcon size={24} />
                <h3>{filtered ? "No matches in this scope" : filters.view === "attention" ? "Nothing needs attention" : "Nothing here right now"}</h3>
                <p>{filtered ? "Loosen a filter or clear the search." : "New messages and comments appear here as they arrive."}</p>
                {filtered && (
                  <Button variant="link" onClick={reset}>
                    Clear filters
                  </Button>
                )}
              </div>
            )}
            {page.isError && (
              <div className="list-empty">
                <h3>Could not load the inbox</h3>
                <Button variant="link" onClick={() => page.refetch()}>
                  Try again
                </Button>
              </div>
            )}
          </div>
          {page.hasNextPage && (
            <div className="list-footer">
              <Button variant="outline" size="sm" disabled={page.isFetchingNextPage} onClick={() => page.fetchNextPage()}>
                {page.isFetchingNextPage ? "Loading…" : `Load older (${Math.max(0, (head?.total ?? 0) - items.length)} more)`}
              </Button>
            </div>
          )}
        </aside>
        <div className="detail-column">
          {outsideView && (
            <div className="outside-view">
              Outside this view.
              <Button variant="link" size="sm" className="h-auto p-0" onClick={reset}>
                Show all work
              </Button>
            </div>
          )}
          <div className="detail-body">
            {welcome ? (
              <section className="inbox-welcome">
                <div className="empty-icon">
                  <InboxIcon size={30} />
                </div>
                <h2>Make the first connection</h2>
                <p>Your shared inbox is ready for a conversation.</p>
                <div className="welcome-steps">
                  {[
                    ["1", "Connect a channel", noChannels ? "Add Instagram or a Facebook Page." : "Done. Your channel is connected."],
                    ["2", "Send a test message", "Use a different customer account."],
                    ["3", "Reply together", "Invite your team when you’re ready."],
                  ].map(([n, t, d]) => (
                    <div key={n}>
                      <span>{n}</span>
                      <div>
                        <strong>{t}</strong>
                        <p>{d}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Button asChild>
                  <Link to="/w/$workspace/settings/channels" params={params}>
                    Check connected channels
                    <ArrowRight />
                  </Link>
                </Button>
                <Button variant="link" asChild>
                  <Link to="/w/$workspace/getting-started" params={params}>
                    View setup checklist
                  </Link>
                </Button>
              </section>
            ) : selectedId ? (
              <Suspense fallback={<ThreadSkeleton />}>
                {selectedKind === "comment" ? <CommentThreadView key={selectedId} commentId={selectedId} onBack={back} onFilterPost={(post) => changeFilters({ post, type: "comments", view: "all" })} /> : <Thread key={selectedId} conversationId={selectedId} onBack={back} />}
              </Suspense>
            ) : (
              <section className="inbox-welcome">
                <div className="empty-icon">
                  <InboxIcon size={30} />
                </div>
                <h2>Pick an item</h2>
                <p>Select a message or comment to read it and reply. Use j and k to move through the list.</p>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function ThreadSkeleton() {
  return (
    <section className="conversation-thread p-6">
      {[1, 2, 3].map((i) => (
        <div className="mb-4 flex gap-3" key={i}>
          <Skeleton className="size-8 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-3 w-full" />
          </div>
        </div>
      ))}
    </section>
  )
}
